import { useEffect, useMemo } from 'react'
import { shallowEqual } from 'react-redux'
import {
  CALENDAR_SYNC_CHECKPOINT_OBJECT,
  getIntegrationServiceIdsForCategory,
  IntegrationCategory,
  IntegrationServiceId,
  ONBOARDING_CALENDAR_SYNC_SUPPORTED_INTEGRATIONS,
} from '../../../../common/integrations'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { useGetMeQuery } from '../../../../store/user/user'
import { syncApi, type CheckpointRequest } from '../../../../store/sync/sync'

const CALENDAR_DIRECT_IDS = getIntegrationServiceIdsForCategory(IntegrationCategory.CALENDAR)

const CHECKPOINT_CALENDAR_SOURCES = ONBOARDING_CALENDAR_SYNC_SUPPORTED_INTEGRATIONS.flatMap((serviceId) => {
  const object = CALENDAR_SYNC_CHECKPOINT_OBJECT[serviceId]
  return object ? [{ serviceId, object }] : []
})

const calendarCheckpointRequest = (accountId: string, serviceId: IntegrationServiceId, object: string): CheckpointRequest => ({
  accountId,
  level: 'user',
  connector: serviceId,
  direction: 'inbound',
  object,
})

export interface CalendarSyncStates {
  calendarSyncActive: Set<IntegrationServiceId>
  isLoading: boolean
}

export const useCalendarSyncStates = (): CalendarSyncStates => {
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()
  const { data: me, isLoading: isMeLoading } = useGetMeQuery({ accountId })

  const connectedIds = useMemo(() => new Set(me?.integrations?.map((i) => i.serviceId) ?? []), [me])

  const hasDirectCalendar = useMemo(() => CALENDAR_DIRECT_IDS.some((id) => connectedIds.has(id)), [connectedIds])

  const checkpointSources = useMemo(
    () => (!accountId || hasDirectCalendar ? [] : CHECKPOINT_CALENDAR_SOURCES.filter(({ serviceId }) => connectedIds.has(serviceId))),
    [accountId, hasDirectCalendar, connectedIds],
  )

  useEffect(() => {
    const subs = checkpointSources.map(({ serviceId, object }) =>
      dispatch(syncApi.endpoints.getCheckpoint.initiate(calendarCheckpointRequest(accountId, serviceId, object))),
    )
    return () => subs.forEach((s) => s.unsubscribe())
  }, [accountId, checkpointSources, dispatch])

  const activeCalendarSyncIntegrationIds = useAppSelector((state) => {
    const directIds = CALENDAR_DIRECT_IDS.filter((id) => connectedIds.has(id))
    const checkpointIds = checkpointSources
      .filter(
        ({ serviceId, object }) =>
          syncApi.endpoints.getCheckpoint.select(calendarCheckpointRequest(accountId, serviceId, object))(state)?.data?.data?.active === true,
      )
      .map(({ serviceId }) => serviceId)
    const isRedtailActive = me?.integrations?.find((i) => i.serviceId === IntegrationServiceId.REDTAIL)?.serviceConfig?.inbound?.calendar?.enabled === true

    return [...directIds, ...checkpointIds, ...(isRedtailActive ? [IntegrationServiceId.REDTAIL] : [])]
  }, shallowEqual)

  const isAnyCheckpointPending = useAppSelector((state) =>
    checkpointSources.some(
      ({ serviceId, object }) => syncApi.endpoints.getCheckpoint.select(calendarCheckpointRequest(accountId, serviceId, object))(state)?.status === 'pending',
    ),
  )

  const calendarSyncActive = useMemo(() => new Set(activeCalendarSyncIntegrationIds), [activeCalendarSyncIntegrationIds])
  const isLoading = isMeLoading || isAnyCheckpointPending

  return useMemo(() => ({ calendarSyncActive, isLoading }), [calendarSyncActive, isLoading])
}
